import React, { useCallback, useState, useMemo } from 'react'

import { useTranslation } from 'react-i18next'

import { Collapse, Switch } from '@mui/material'

import { DiseaseGroupFieldProps, Disease } from '../../../ana-toolbox'
import { getQuestionTranslationKey } from '../../utils/helpers/DiseaseType'
import { RadioFormLabel } from '../../utils/shared/RadioFormLabel'
import DiseaseField from '../disease-field/disease-field'
import { FieldHelperText } from '../field-helper-text/field-helper-text'
import { Translate } from '../translate/translate'

import { isSameName, isDifferentName, getOtherDiseaseForType } from './disease-group-field.helpers'
import * as S from './disease-group-field.styles'

export const DiseaseGroupField = ({
  diseases,
  label,
  type,
  customInputPlaceholder,
  hideMedications,
  error,
  selectedDiseases = [],
  allDiseases = [],
  activeHelperText,
  checked,
  onToggle,
  onDiseaseChange,
  onDiseaseCheck,
}: DiseaseGroupFieldProps) => {
  const { t } = useTranslation()

  const [otherDiseases, setOtherDiseases] = useState<Disease[]>([
    ...getOtherDiseaseForType(type, selectedDiseases, allDiseases),
    { type, name: '', medications: [] },
  ])

  const helperText = useMemo(() => {
    if (checked) {
      return t('field.select_relevant')
    }

    if (activeHelperText) return t(activeHelperText)

    if (!diseases.length) return

    return diseases
      .slice(0, 2)
      .map((disease) => t(`option.${disease.name}`))
      .join(', ')
      .concat(',...')
  }, [diseases, checked, t, activeHelperText])

  const handleDiseaseCheck = useCallback(
    (event: React.ChangeEvent<{ name: string }>, marked: boolean) => {
      const diseaseName = event.target.name
      const disease = allDiseases.find(isSameName(diseaseName)) || otherDiseases.find(isSameName(diseaseName))

      // No disease was found with the event target name
      if (!disease) return

      const newSelectedDiseases = marked
        ? [...selectedDiseases, disease]
        : selectedDiseases.filter(isDifferentName(diseaseName))

      if (onDiseaseCheck) onDiseaseCheck(newSelectedDiseases)
    },
    [allDiseases, selectedDiseases, onDiseaseCheck, otherDiseases]
  )

  const handleDiseaseChange = useCallback(
    (updatedDisease: Disease) => {
      const newSelectedDiseases = selectedDiseases.map((currentDisease) => {
        if (updatedDisease.name === currentDisease.name) return updatedDisease

        return currentDisease
      })

      if (onDiseaseChange) onDiseaseChange(updatedDisease, newSelectedDiseases)
    },
    [onDiseaseChange, selectedDiseases]
  )

  const handleCustomDiseaseChange = useCallback(
    (index: number) => (updatedDisease: Disease) => {
      const otherDiseasesIndex = index - diseases.length

      setOtherDiseases((old) => {
        const isNew = !old.some(isSameName(updatedDisease.name))

        const newOtherDiseases = old
          .map((currentDisease, i) => {
            return i === otherDiseasesIndex ? updatedDisease : currentDisease
          })
          .filter((currentDisease) => currentDisease.name !== '')

        /* Keep only the selected diseases wich are in the `allDiseases` or
         * are in the `otherDiseases` (custom) list.
         */
        const newSelectedDiseases = selectedDiseases
          .filter((selectedDisease) => {
            return (
              selectedDisease.name !== '',
              /* we keep all the diseases of different type
               * because it's not related to this disease group.
               */
              selectedDisease.type !== type ||
                /*
                 * Then we need to make sure that the all the selected diseases exists in either the other
                 * diseases list or all disease list. If it's not included in any of those lists, we throw
                 * it away because it's a stale date inserted when the user was typing.
                 * Ex.: the user types "A", then "B", then "C". The input value now is "ABC" so we need to make sure
                 * that "A" and "AB" is not in the list anymore.
                 */
                newOtherDiseases.some(isSameName(selectedDisease.name)) ||
                allDiseases.some(isSameName(selectedDisease.name))
            )
          })
          /* There's a case where we can match the updated disease with a disease in the
           * selected diseases list, it happens when the user changes the medications for disease,
           * then we need to make sure that we update the selected disease with the
           * new updated disease containing the updated medications.
           */
          .map((selectedDisease) => (selectedDisease.name === updatedDisease.name ? updatedDisease : selectedDisease))

        if (onDiseaseChange) {
          onDiseaseChange(updatedDisease, isNew ? [...newSelectedDiseases, updatedDisease] : newSelectedDiseases)
        }

        return [...newOtherDiseases, { type, name: '', medications: [] }]
      })
    },
    [onDiseaseChange, selectedDiseases, allDiseases, type, diseases.length]
  )

  const handleDeleteCustomDisease = useCallback(
    (disease: Disease) => {
      const isDifferent = (currentDisease: Disease) =>
        currentDisease.name !== disease.name || disease.type !== currentDisease.type

      setOtherDiseases((old) => old.filter(isDifferent))

      if (onDiseaseChange) {
        onDiseaseChange(disease, selectedDiseases.filter(isDifferent))
      }
    },
    [selectedDiseases, onDiseaseChange, setOtherDiseases]
  )

  const diseasesWithMetadata = useMemo(() => {
    const normalDiseases: Disease[] = diseases.map((disease) => ({
      ...disease,
      custom: false,
    }))

    const customDiseases: Disease[] = otherDiseases.map((disease) => ({
      ...disease,
      custom: true,
    }))

    return [...normalDiseases, ...customDiseases]
  }, [diseases, otherDiseases])

  const translationKey = getQuestionTranslationKey(type)

  return (
    <div>
      {label && (
        <RadioFormLabel>
          <Translate>{label}</Translate>
        </RadioFormLabel>
      )}

      <S.ToggleContainer checked={checked}>
        <S.FormControlLabel
          label={t(`${translationKey}_v2`, {
            defaultValue: t(translationKey, {
              postProcess: [],
            }),
          })}
          control={<Switch name={type} checked={checked} onChange={onToggle} />}
          labelPlacement="start"
        />
      </S.ToggleContainer>

      {!checked && <FieldHelperText name={type} helperText={helperText} />}

      {checked && (
        <Collapse in={checked}>
          <S.DiseasesContainer>
            {diseasesWithMetadata.map((disease, index) => {
              const selectedDisease = selectedDiseases?.find((sd) => sd.name === disease.name)

              return (
                <DiseaseField
                  key={`disease-group-${index}`}
                  index={index}
                  hideMedications={hideMedications}
                  disease={selectedDisease || disease}
                  checked={Boolean(selectedDisease)}
                  customInputPlaceholder={customInputPlaceholder}
                  autoFocus={diseases.length === 0 && otherDiseases.length === 0}
                  isCustom={disease.custom}
                  isLast={index === diseasesWithMetadata.length - 1}
                  onCheck={handleDiseaseCheck}
                  onChange={disease.custom ? handleCustomDiseaseChange(index) : handleDiseaseChange}
                  onDelete={handleDeleteCustomDisease}
                />
              )
            })}
          </S.DiseasesContainer>
        </Collapse>
      )}

      {error && <FieldHelperText name={type} error={error} />}
    </div>
  )
}
